import { ProcurementTypeItemFragment } from 'generated/resources/graphql'

import { Form, SectionElement } from 'modules/forms'
import { Element } from 'modules/forms/lib/Element'
import {
  QuestionnaireType,
  QuestionType,
  SectionType,
} from 'modules/forms/components/newFormEditor/types'

function returnOrGenerateId(id?: string): string {
  return id || Math.random().toString()
}

type ElementType = QuestionType | SectionType

function isSection(element: ElementType): element is SectionType {
  return element.type === 'section'
}

function elementToFragment(element: ElementType, formId: string, sectionId?: string) {
  return {
    id: returnOrGenerateId(element.id),
    invariantId: ('invariantId' in element ? element.invariantId : undefined) ?? crypto.randomUUID(),
    label: element.label,
    description: element.description || '',
    formId,
    sectionId: sectionId || ('sectionId' in element ? element.sectionId : '') || '',
    type: element.type,
    position: element.position,
    mandatory: element.mandatory,
    locked: element.locked,
    hasCorrectAnswer: element.hasCorrectAnswer ?? false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    options: (element.options || []).map((option) => ({
      ...option,
      id: returnOrGenerateId(option.id),
    })),
    params: { ...(element.params || {}), locked: element.locked ?? null },
  }
}

export function flattenElements(elements: ElementType[], formId: string) {
  return elements.flatMap((element) => {
    const fragment = elementToFragment(element, formId)
    const flattenedElements = [fragment]

    if (isSection(element)) {
      const children = element.elements.map((child) =>
        elementToFragment(child, formId, fragment.id)
      )
      flattenedElements.push(...children)
    }

    return flattenedElements
  })
}

interface PreviewFormData {
  id: string
  anonymous: boolean
  resourceId?: string
}

export function generatePreview(
  requestType: ProcurementTypeItemFragment,
  formData: PreviewFormData,
  formState: QuestionnaireType
): ProcurementTypeItemFragment {
  const formId = returnOrGenerateId(formData.id)

  return {
    ...requestType,
    formsConnection: {
      nodes: [
        {
          status: 'draft' as const,
          formType: 'custom' as const,
          resourceType: 'Procurement::Type' as const,
          id: formId,
          anonymous: formData.anonymous,
          description: formState.description || '',
          resourceId: formData.resourceId || '',
          title: formState.title,
          elements: flattenElements(formState.elements, formId),
        },
      ],
    },
  }
}

function formElementToFragment(element: Element, formId: string, sectionId?: string) {
  return {
    ...element,
    id: returnOrGenerateId(element.id),
    invariantId: element.invariantId ?? crypto.randomUUID(),
    description: element.description || '',
    formId,
    sectionId: sectionId || element.sectionId || '',
    type: element.getType(),
    createdAt: element.createdAt || new Date().toISOString(),
    updatedAt: element.updatedAt || new Date().toISOString(),
    options: element.options.map((option) => ({
      ...option,
      id: returnOrGenerateId(option.id),
    })),
    params: element.params || {},
  }
}

function flattenFormElements(elements: Element[], formId: string) {
  return elements.flatMap((element) => {
    const fragment = formElementToFragment(element, formId)
    const flattenedElements = [fragment]

    if (element instanceof SectionElement) {
      const children = element.elements.map((child) =>
        formElementToFragment(child, formId, fragment.id)
      )
      flattenedElements.push(...children)
    }

    return flattenedElements
  })
}

export function generatePreviewFromForm(
  requestType: ProcurementTypeItemFragment,
  form: Form
): ProcurementTypeItemFragment {
  const formId = returnOrGenerateId(form.id)

  return {
    ...requestType,
    formsConnection: {
      nodes: [
        {
          status: 'draft' as const,
          formType: 'custom' as const,
          resourceType: 'Procurement::Type' as const,
          id: formId,
          anonymous: false,
          description: form.description || '',
          resourceId: form.resourceId || '',
          title: form.title,
          elements: flattenFormElements(form.elements, formId),
        },
      ],
    },
  }
}
